import { useState } from 'react';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Card from '@/components/Card';
import FormElement from '@/components/FormElement';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import TextArea from '@/components/TextArea';
import { Body } from '@/components/Typography';

// Mirror graphy's BackgroundCheck::OverrideService::MAX_REASON_LENGTH.
const MAX_REASON_LENGTH = 1000;

type Props = {
  // GRAVY-10976: the worker never started a Checkr check.
  withoutCheckrRecord?: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
};

const OverrideBackgroundCheckModal = ({
  withoutCheckrRecord = false,
  onClose,
  onSubmit,
}: Props) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!reason.trim()) return;

    setLoading(true);
    setError(null);

    try {
      await onSubmit(reason.trim());
      onClose();
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to override background check',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      disableClickout
      size="sm"
      title="Override Background Check"
      onRequestClose={onClose}
    >
      {withoutCheckrRecord && (
        <Card.Section>
          <Alert
            description="This worker has never started a Checkr background check. Overriding clears someone who has not been checked at all. Do this only if you have another way to confirm they passed a check, and say how in the reason."
            icon={faExclamationTriangle}
            status="danger"
            title="No Checkr check on file"
          />
        </Card.Section>
      )}

      <Card.Section>
        <Body>
          Use this only when the worker has passed a background check that
          Checkr cannot confirm right now, for example during a Checkr outage.
          They will be able to claim shifts that require a background check{' '}
          <strong>until an admin changes their background status</strong>. Later
          Checkr results do not remove the override. Applying it sends the
          worker nothing; once they are ready to work, they get the usual
          &quot;cleared to work&quot; message.
        </Body>
      </Card.Section>

      <Card.Section>
        <Stack vertical gap={12}>
          {error && (
            <Alert
              description={error}
              icon={faExclamationTriangle}
              status="danger"
              title="Error"
            />
          )}

          <FormElement label="Reason (required)" required showRequiredIndicator>
            <TextArea
              data-testid="override-background-check-reason"
              maxLength={MAX_REASON_LENGTH}
              placeholder="e.g. Checkr outage, report shows complete in the Checkr dashboard"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
            />
          </FormElement>
        </Stack>
      </Card.Section>

      <Card.Section>
        <Stack justify="end">
          <Button
            a11yLabel="Cancel background check override"
            appearance="outline"
            label="Cancel"
            onClick={onClose}
          />
          <Button
            a11yLabel="Confirm background check override"
            id="btn-confirm-override-background-check"
            isLoading={loading}
            label="Confirm Override"
            status="danger"
            onClick={handleSubmit}
            disabled={!reason.trim()}
          />
        </Stack>
      </Card.Section>
    </Modal>
  );
};

export default OverrideBackgroundCheckModal;
